import * as PaymentsService from '~/services/PaymentsService';
import { ReservationRate } from '~/services/ReservationService';
import { postConvertedCurrency } from '~/services/fxService';

// Constants for repeated values
export const BASE_PACKAGE_TYPE = 'Base Package';
export const PEAK_PERIOD_TYPE = 'Peak Period';
export const REVENUE_MANAGEMENT_TYPE = 'Revenue Management';
export const EXTRA_GUEST_TYPE = 'Extra Guest';

export const DEFAULT_SURCHARGE_TYPES = [
  BASE_PACKAGE_TYPE,
  PEAK_PERIOD_TYPE,
  REVENUE_MANAGEMENT_TYPE,
  EXTRA_GUEST_TYPE,
] as const;

export const SURCHARGE_TYPE_MAPPINGS = {
  [PEAK_PERIOD_TYPE]: 'peak_period_surcharge',
  [EXTRA_GUEST_TYPE]: 'extra_guest_surcharge',
  [REVENUE_MANAGEMENT_TYPE]: 'internal_surcharge',
  [BASE_PACKAGE_TYPE]: 'nightly_rate',
} as const;

// Configuration constants
export const ENABLE_CURRENCY_CONVERSION = true;
export const DISABLE_CURRENCY_CONVERSION = false;

export interface SurchargeData {
  surchargeCostPrice: number;
  surchargeSellPrice: number;
  surchargeInternalSellAmount: number;
  totalSurchargeCostPrice: number;
  totalSurchargeSellPrice: number;
  totalSurchargeInternalSellAmount: number;
  hasPeakPeriodSurcharge: boolean;
  hasExtraGuestSurcharge: boolean;
  hasInternalSurcharge: boolean;
  // Package cost tracking
  packageCostPrice: number;
  packageSellPrice: number;
  totalPackageCostPrice: number;
  totalPackageSellPrice: number;
}

export interface DisabledRefundTypes {
  nightly_rate: boolean;
  peak_period_surcharge: boolean;
  extra_guest_surcharge: boolean;
  internal_surcharge: boolean;
  isDisabled: boolean;
}

export interface SurchargePrices {
  extra_guest_surcharge_sell_amount: number;
  peak_period_surcharge_sell_amount: number;
  surcharge_internal_sell_amount: number;
}

export interface NightPrices {
  packagePrice: number;
  surcharges?: SurchargePrices;
  totalSurcharge: number;
  originalPackagePrice?: number;
  originalSurcharges?: SurchargePrices;
}

/**
 * Helper function to check if a surcharge type should be included
 */
function shouldIncludeSurchargeType(
  surchargeType: string,
  selectedSurchargeTypes: Array<string>,
  disabledType: DisabledRefundTypes | undefined,
): boolean {
  if (!selectedSurchargeTypes.includes(surchargeType)) {
    return false;
  }

  const disabledField = SURCHARGE_TYPE_MAPPINGS[surchargeType as keyof typeof SURCHARGE_TYPE_MAPPINGS];
  return !disabledType?.[disabledField as keyof DisabledRefundTypes];
}

/**
 * Utility function to check if a surcharge type is available (not disabled)
 * Used to reduce repetitive switch statements
 */
export function isSurchargeTypeAvailable(
  surchargeType: string,
  disabledTypes: DisabledRefundTypes | undefined,
): boolean {
  if (!disabledTypes) {
    return true;
  }

  const disabledField = SURCHARGE_TYPE_MAPPINGS[surchargeType as keyof typeof SURCHARGE_TYPE_MAPPINGS];
  return !disabledTypes[disabledField as keyof DisabledRefundTypes];
}

/**
 * Utility function to check if any of the selected surcharge types are available
 */
export function hasAvailableSurchargeTypes(
  selectedSurchargeTypes: Array<string>,
  disabledTypes: DisabledRefundTypes | undefined,
): boolean {
  if (!disabledTypes) {
    return true;
  }

  return selectedSurchargeTypes.some((type) => isSurchargeTypeAvailable(type, disabledTypes));
}

/**
 * Currency conversion utility
 */
async function convertSurchargeCurrency(
  surchargeCostPrice: number,
  totalSurchargeCostPrice: number,
  costCurrency: string,
  sellCurrency: string,
): Promise<{ convertedSelected: number; convertedTotal: number }> {
  if (costCurrency === sellCurrency || surchargeCostPrice === 0) {
    return {
      convertedSelected: surchargeCostPrice,
      convertedTotal: totalSurchargeCostPrice,
    };
  }

  const payload = {
    total: surchargeCostPrice,
    baseCurrencyCode: costCurrency,
    targetCurrencyCode: sellCurrency,
  };

  const totalPayload = {
    total: totalSurchargeCostPrice,
    baseCurrencyCode: costCurrency,
    targetCurrencyCode: sellCurrency,
  };

  const [convertedSelectedResult, convertedTotalResult] = await Promise.all([
    postConvertedCurrency(payload),
    postConvertedCurrency(totalPayload),
  ]);

  return {
    convertedSelected: convertedSelectedResult.result.convertedTotal,
    convertedTotal: convertedTotalResult.result.convertedTotal,
  };
}

/**
 * Core function that processes reservation surcharges with built-in currency conversion
 *
 * @param reservationRates - Array of reservation rate data
 * @param selectedNightsForCalculation - Set of selected night dates (ISO strings) to calculate surcharge totals for.
 *                                     Pass empty Set() to skip selected night calculations and only get price map data.
 * @param selectedSurchargeTypes - Which surcharge types to include in calculations
 * @param disabledRefundTypes - Already refunded types to exclude from calculations
 * @param shouldConvertCurrency - Whether to convert cost prices from cost currency to sell currency
 *
 * @returns Object with:
 *   - surchargeData: Calculated totals for selected nights (if any selected)
 *   - priceMap: Price data for ALL nights (for display purposes)
 *   - availableSurcharges: Total surcharge amounts across all nights
 */
export async function processReservationSurcharges(
  reservationRates: Array<ReservationRate>,
  selectedNightsForCalculation: Set<string> = new Set(),
  selectedSurchargeTypes: Array<string> = [...DEFAULT_SURCHARGE_TYPES],
  disabledRefundTypes: { [key: string]: DisabledRefundTypes } = {},
  shouldConvertCurrency: boolean,
): Promise<{
  surchargeData: SurchargeData;
  priceMap: { [key: string]: NightPrices };
  availableSurcharges: SurchargePrices;
}> {
  let surchargeCostPrice = 0;
  let surchargeSellPrice = 0;
  let surchargeInternalSellAmount = 0;
  let totalSurchargeCostPrice = 0;
  let totalSurchargeSellPrice = 0;
  let totalSurchargeInternalSellAmount = 0;
  let hasPeakPeriodSurcharge = false;
  let hasExtraGuestSurcharge = false;
  let hasInternalSurcharge = false;

  // Package cost tracking
  let selectedPackageCostPrice = 0;
  let selectedPackageSellPrice = 0;
  let totalPackageCostPrice = 0;
  let totalPackageSellPrice = 0;

  const priceMap: { [key: string]: NightPrices } = {};
  const availableSurcharges: SurchargePrices = {
    extra_guest_surcharge_sell_amount: 0,
    peak_period_surcharge_sell_amount: 0,
    surcharge_internal_sell_amount: 0,
  };

  if (reservationRates.length > 0) {
    // First pass: Calculate totals and build price map for ALL nights
    reservationRates.forEach((rate) => {
      const dateKey = new Date(rate.date).toISOString();
      const disabledType = disabledRefundTypes[dateKey];

      // Calculate original amounts (before filtering)
      const originalPeakAmount = rate.peak_period_surcharge_sell_amount || 0;
      const originalExtraGuestAmount = rate.extra_guest_surcharge_sell_amount || 0;
      const originalInternalAmount = rate.surcharge_internal_sell_amount || 0;

      // Apply disabled filtering for calculations
      const availablePeakAmount = disabledType?.peak_period_surcharge ? 0 : originalPeakAmount;
      const availableExtraGuestAmount = disabledType?.extra_guest_surcharge ? 0 : originalExtraGuestAmount;
      const availableInternalAmount = disabledType?.internal_surcharge ? 0 : originalInternalAmount;
      const availablePackagePrice = disabledType?.nightly_rate ? 0 : rate.sell_amount;

      // Build price map with both original and filtered values
      const surcharges: SurchargePrices = {
        extra_guest_surcharge_sell_amount: availableExtraGuestAmount,
        peak_period_surcharge_sell_amount: availablePeakAmount,
        surcharge_internal_sell_amount: availableInternalAmount,
      };

      const originalSurcharges: SurchargePrices = {
        extra_guest_surcharge_sell_amount: originalExtraGuestAmount,
        peak_period_surcharge_sell_amount: originalPeakAmount,
        surcharge_internal_sell_amount: originalInternalAmount,
      };

      priceMap[dateKey] = {
        packagePrice: availablePackagePrice,
        surcharges,
        totalSurcharge: availablePeakAmount + availableExtraGuestAmount + availableInternalAmount,
        originalPackagePrice: rate.sell_amount,
        originalSurcharges,
      };

      // Accumulate available totals (for column visibility)
      availableSurcharges.extra_guest_surcharge_sell_amount += originalExtraGuestAmount;
      availableSurcharges.peak_period_surcharge_sell_amount += originalPeakAmount;
      availableSurcharges.surcharge_internal_sell_amount += originalInternalAmount;

      // Accumulate grand totals (filtered amounts)
      totalSurchargeCostPrice +=
        (rate.peak_period_surcharge_cost_amount || 0) + (rate.extra_guest_surcharge_cost_amount || 0);

      totalSurchargeSellPrice += availablePeakAmount + availableExtraGuestAmount;
      totalSurchargeInternalSellAmount += availableInternalAmount;

      // Accumulate package costs (filtered amounts)
      totalPackageCostPrice += rate.cost_amount || 0;
      totalPackageSellPrice += rate.sell_amount || 0;

      // Track surcharge availability (for any night, even if some are disabled)
      hasPeakPeriodSurcharge ||= !!originalPeakAmount;
      hasExtraGuestSurcharge ||= !!originalExtraGuestAmount;
      hasInternalSurcharge ||= !!originalInternalAmount;
    });

    // Second pass: Calculate selected amounts (only if specific nights are selected)
    if (selectedNightsForCalculation.size > 0) {
      const selectedDates = Array.from(selectedNightsForCalculation);

      selectedDates.forEach((dateString) => {
        const rate = reservationRates.find((r) => new Date(r.date).toISOString() === dateString);
        if (rate) {
          const disabledType = disabledRefundTypes[dateString];

          // Use helper function to reduce repetition
          if (shouldIncludeSurchargeType(PEAK_PERIOD_TYPE, selectedSurchargeTypes, disabledType)) {
            surchargeCostPrice += rate.peak_period_surcharge_cost_amount || 0;
            surchargeSellPrice += rate.peak_period_surcharge_sell_amount || 0;
          }

          if (shouldIncludeSurchargeType(EXTRA_GUEST_TYPE, selectedSurchargeTypes, disabledType)) {
            surchargeCostPrice += rate.extra_guest_surcharge_cost_amount || 0;
            surchargeSellPrice += rate.extra_guest_surcharge_sell_amount || 0;
          }

          if (shouldIncludeSurchargeType(REVENUE_MANAGEMENT_TYPE, selectedSurchargeTypes, disabledType)) {
            surchargeInternalSellAmount += rate.surcharge_internal_sell_amount || 0;
          }

          // Calculate package costs for selected nights
          if (shouldIncludeSurchargeType(BASE_PACKAGE_TYPE, selectedSurchargeTypes, disabledType)) {
            selectedPackageCostPrice += rate.cost_amount || 0;
            selectedPackageSellPrice += rate.sell_amount || 0;
          }
        }
      });
    }

    // Built-in currency conversion
    if (shouldConvertCurrency) {
      const costCurrency = reservationRates[0]?.cost_currency;
      const sellCurrency = reservationRates[0]?.sell_currency;

      if (costCurrency && sellCurrency && costCurrency !== sellCurrency && surchargeCostPrice > 0) {
        const { convertedSelected, convertedTotal } = await convertSurchargeCurrency(
          surchargeCostPrice,
          totalSurchargeCostPrice,
          costCurrency,
          sellCurrency,
        );

        surchargeCostPrice = convertedSelected;
        totalSurchargeCostPrice = convertedTotal;
      }
    }
  }

  return {
    surchargeData: {
      surchargeCostPrice,
      surchargeSellPrice,
      surchargeInternalSellAmount,
      totalSurchargeCostPrice,
      totalSurchargeSellPrice,
      totalSurchargeInternalSellAmount,
      hasPeakPeriodSurcharge,
      hasExtraGuestSurcharge,
      hasInternalSurcharge,
      // Package cost data
      packageCostPrice: selectedPackageCostPrice,
      packageSellPrice: selectedPackageSellPrice,
      totalPackageCostPrice,
      totalPackageSellPrice,
    },
    priceMap,
    availableSurcharges,
  };
}

export async function loadDisabledRefundTypes(
  itemId: string,
  orderId: string,
): Promise<{ [key: string]: DisabledRefundTypes }> {
  const disabledRefundTypes: { [key: string]: DisabledRefundTypes } = {};
  const refundMetaResponse = await PaymentsService.getRefundMeta(orderId);

  if (refundMetaResponse?.result) {
    refundMetaResponse.result
      .filter((refund) => refund.fk_items === itemId)
      .forEach((refund) => {
        const selectedNights = refund.additional_info?.selected_nights || {};

        Object.entries(selectedNights).forEach(([night, refundTypes]: [string, any]) => {
          const dateKey = new Date(night).toISOString();

          if (!disabledRefundTypes[dateKey]) {
            disabledRefundTypes[dateKey] = {
              nightly_rate: false,
              peak_period_surcharge: false,
              extra_guest_surcharge: false,
              internal_surcharge: false,
              isDisabled: false,
            };
          }

          // Mark refund types as disabled if they were refunded (true in the data)
          if (refundTypes.nightly_rate) {
            disabledRefundTypes[dateKey].nightly_rate = true;
          }
          if (refundTypes.peak_period_surcharge) {
            disabledRefundTypes[dateKey].peak_period_surcharge = true;
          }
          if (refundTypes.extra_guest_surcharge) {
            disabledRefundTypes[dateKey].extra_guest_surcharge = true;
          }
          if (refundTypes.internal_surcharge) {
            disabledRefundTypes[dateKey].internal_surcharge = true;
          }

          // Check if all types are disabled (entire row should be disabled)
          const allTypesDisabled =
            disabledRefundTypes[dateKey].nightly_rate &&
            disabledRefundTypes[dateKey].peak_period_surcharge &&
            disabledRefundTypes[dateKey].extra_guest_surcharge &&
            disabledRefundTypes[dateKey].internal_surcharge;

          disabledRefundTypes[dateKey].isDisabled = allTypesDisabled;
        });
      });
  }

  return disabledRefundTypes;
}
